import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import Lowlight from "react-lowlight";

import { atlasRoutes } from "@/app/router/routes";
import { fetchNoteSuggestionInternal } from "@/pages/note-suggestions/shared/api";

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IdText = styled.span`
  font-family: "Berkeley Mono Variable", monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
`;

const Section = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
`;

const FieldValue = styled.span`
  font-size: 14px;
  color: #1e293b;
`;

const KindBadge = styled.span<{ kind: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ kind }) => {
    if (kind.includes("cleanup")) return "#dbeafe";
    if (kind.includes("refine")) return "#dcfce7";
    if (kind.includes("voice")) return "#fef3c7";
    return "#f1f5f9";
  }};
  color: ${({ kind }) => {
    if (kind.includes("cleanup")) return "#1d4ed8";
    if (kind.includes("refine")) return "#166534";
    if (kind.includes("voice")) return "#92400e";
    return "#475569";
  }};
`;

const StyledLink = styled(Link)`
  color: #3b82f6;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ContentBlock = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  font-size: 12px;
  max-height: 300px;
  overflow-y: auto;
`;

const LoadingState = styled.div`
  padding: 48px;
  text-align: center;
  color: #64748b;
`;

const ErrorState = styled.div`
  padding: 48px;
  text-align: center;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 8px;
`;

export const NoteSuggestionsViewPage = () => {
  const { noteSuggestionId } = useParams<{ noteSuggestionId: string }>();

  const query = useQuery({
    queryKey: ["note-suggestion", noteSuggestionId],
    queryFn: () => fetchNoteSuggestionInternal(noteSuggestionId!),
    enabled: Boolean(noteSuggestionId),
  });

  if (query.isLoading) {
    return (
      <PageContainer>
        <LoadingState>Loading note suggestion...</LoadingState>
      </PageContainer>
    );
  }

  if (query.isError || !query.data) {
    return (
      <PageContainer>
        <ErrorState>Failed to load note suggestion or note suggestion not found.</ErrorState>
      </PageContainer>
    );
  }

  const noteSuggestion = query.data;

  return (
    <PageContainer>
      <Header>
        <Title>Note Suggestion</Title>
        <Subtitle>
          <IdText>{noteSuggestion.id}</IdText>
          <KindBadge kind={noteSuggestion.kind}>{noteSuggestion.kind}</KindBadge>
        </Subtitle>
      </Header>

      <Section>
        <SectionTitle>Details</SectionTitle>
        <Grid>
          <Field>
            <FieldLabel>Space Account</FieldLabel>
            <FieldValue>
              <StyledLink
                to={
                  atlasRoutes.spaceAccountsView({
                    params: { spaceAccountId: noteSuggestion.space_account_id },
                  }).path
                }
              >
                {noteSuggestion.space_account_id}
              </StyledLink>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Note</FieldLabel>
            <FieldValue>
              <StyledLink
                to={
                  atlasRoutes.notesView({
                    params: { noteId: noteSuggestion.note_id },
                  }).path
                }
              >
                {noteSuggestion.note_id}
              </StyledLink>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Space Account Note ID</FieldLabel>
            <FieldValue>
              <IdText>{noteSuggestion.space_account_note_id}</IdText>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Kind</FieldLabel>
            <FieldValue>
              <KindBadge kind={noteSuggestion.kind}>{noteSuggestion.kind}</KindBadge>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Created At</FieldLabel>
            <FieldValue>{new Date(noteSuggestion.created_at).toLocaleString()}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Updated At</FieldLabel>
            <FieldValue>{new Date(noteSuggestion.updated_at).toLocaleString()}</FieldValue>
          </Field>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>Labels</SectionTitle>
        <Grid>
          <Field>
            <FieldLabel>Suggested Primary Label</FieldLabel>
            <FieldValue>{noteSuggestion.suggested_primary_label || "(empty)"}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Suggested Secondary Label</FieldLabel>
            <FieldValue>{noteSuggestion.suggested_secondary_label || "(empty)"}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Previous Primary Label</FieldLabel>
            <FieldValue>{noteSuggestion.prev_primary_label || "(empty)"}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Previous Secondary Label</FieldLabel>
            <FieldValue>{noteSuggestion.prev_secondary_label || "(empty)"}</FieldValue>
          </Field>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>Suggested Markdown Content</SectionTitle>
        <ContentBlock>
          <Lowlight
            language="markdown"
            value={noteSuggestion.suggested_markdown_content || "(empty)"}
            markers={[]}
          />
        </ContentBlock>
      </Section>

      <Section>
        <SectionTitle>Previous Markdown Content</SectionTitle>
        <ContentBlock>
          <Lowlight
            language="markdown"
            value={noteSuggestion.prev_markdown_content || "(empty)"}
            markers={[]}
          />
        </ContentBlock>
      </Section>

      <Section>
        <SectionTitle>Metadata</SectionTitle>
        <ContentBlock>
          <Lowlight
            language="json"
            value={JSON.stringify(noteSuggestion.metadata, null, 2)}
            markers={[]}
          />
        </ContentBlock>
      </Section>
    </PageContainer>
  );
};
